import React from "react";
import { Plan } from "../../../../Entities/Plan";
import { Subscription } from "../../../../Entities/Subscription";
import { Typography, List, Divider, Button, Alert } from "antd";
import {
  BgColorsOutlined,
  GlobalOutlined,
  FileTextOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import useCountUp from "../../../../Utils/countUpAnimation";
import { Limit } from "../../../../Entities/Plan";
import { User } from "../../../../Entities/User";
import PlanDetails from "./PlanDetails";
import { useNavigate } from "react-router-dom";
import { CurrentAccessAddon } from "../../../../Hooks/useAccess";
import { useAppContext } from "../../../../Context/AppContext/AppContext";
import { formatPlanName } from "../../../../Utils/planUtils";
import dayjs from "dayjs";

interface SubscriptionPlanDetailsProps {
  plan: Plan;
  subscription: Subscription;
  addons: CurrentAccessAddon[];
  limits: Limit;
  user: User | null;
  subscriptionMode: "base" | "credit" | "free";
  paidSubscriptions: Subscription[];
}

const SubscriptionPlanDetails: React.FC<SubscriptionPlanDetailsProps> = ({
  plan,
  subscription,
  addons,
  limits,
  user,
  subscriptionMode,
  paidSubscriptions,
}) => {
  const navigate = useNavigate();
  const { plans } = useAppContext();

  const { count: recruitersCount } = useCountUp(
    subscription?.measurements?.teamMembers || 0
  );
  const { count: applicationsCount } = useCountUp(
    subscription?.measurements?.applicationsProcessed || 0
  );
  const { count: jobPostsCount } = useCountUp(
    subscription?.measurements?.jobPosts || 0
  );

  const { count: activeAddonsCount } = useCountUp(addons.length);

  const data = [
    {
      title: (
        <div className="subscription-plan-details-content-item">
          <Typography.Text>Custom Domain Linking : </Typography.Text>
          {limits.customDomainLinking ? (
            <CheckCircleOutlined
              style={{ color: "#52c41a", fontSize: "14px" }}
            />
          ) : (
            <CloseCircleOutlined
              style={{ color: "#ff4d4f", fontSize: "14px" }}
            />
          )}
        </div>
      ),
      icon: GlobalOutlined,
    },
    {
      title: (
        <div className="subscription-plan-details-content-item">
          <Typography.Text>Custom Branding : </Typography.Text>
          {limits.customBranding ? (
            <CheckCircleOutlined
              style={{ color: "#52c41a", fontSize: "14px" }}
            />
          ) : (
            <CloseCircleOutlined
              style={{ color: "#ff4d4f", fontSize: "14px" }}
            />
          )}
        </div>
      ),
      icon: BgColorsOutlined,
    },
    {
      title: (
        <div className="subscription-plan-details-content-item">
          <Typography.Text>
            No of Active Job Posts{" "}
            {subscriptionMode === "base" && <small>(incl addons)</small>} :{" "}
          </Typography.Text>
          {limits.jobPosts === -1 ? (
            <Typography.Text type="secondary" strong>
              Unlimited
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" strong>
              {limits.jobPosts}
            </Typography.Text>
          )}
        </div>
      ),
      icon: FileTextOutlined,
    },
    {
      title: (
        <div className="subscription-plan-details-content-item">
          <Typography.Text>No of Recruiters : </Typography.Text>
          {limits.teamMembers === -1 ? (
            <Typography.Text type="secondary" strong>
              Unlimited
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" strong>
              {limits.teamMembers}
            </Typography.Text>
          )}
        </div>
      ),
      icon: TeamOutlined,
    },
  ];

  if (subscriptionMode !== "credit") {
    data.push({
      title: (
        <div className="subscription-plan-details-content-item">
          <Typography.Text>
            No of Assessments{" "}
            {subscriptionMode === "base" && <small>(incl addons)</small>} :{" "}
          </Typography.Text>
          {limits.applicationsProcessed === -1 ? (
            <Typography.Text type="secondary" strong>
              Unlimited
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" strong>
              {limits.applicationsProcessed}
            </Typography.Text>
          )}
        </div>
      ),
      icon: FileTextOutlined,
    });
  }

  const handleViewPricing = () => {
    navigate("/pricing");
  };

  const paidSubscription = paidSubscriptions[0] || null;

  const getUpcomingSubscriptionMessage = (
    planId: string,
    startDate: Date,
    mode: string
  ) => {
    const plan = plans.find((p) => p._id === planId);
    if (!plan && mode === "base") {
      return null;
    }
    const planName = formatPlanName(
      plan ? plan?.name : "QualifyMe Credit Plan"
    );
    const formattedStartDate = dayjs(startDate).format("DD MMM YYYY");
    return `You have a paid subscription for ${planName} (${mode}-based) from ${formattedStartDate}`;
  };

  return (
    <div className="subscription-plan-details-container ">
      <div className="subscription-plan-details-content">
        <Divider>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Plan & Subscription Details
          </Typography.Title>
        </Divider>

        {paidSubscription && (
          <Alert
            style={{ width: "100%" }}
            type="info"
            message="Upcoming Subscription"
            description={getUpcomingSubscriptionMessage(
              paidSubscription.planId,
              paidSubscription.startDate,
              paidSubscription.mode
            )}
          />
        )}

        <PlanDetails
          plan={plan}
          subscription={subscription}
          addons={addons}
          limits={limits}
          user={user}
          subscriptionMode={subscriptionMode}
        />

        {user && user.role === "admin" && (
          <div className="subscription-plan-details-content-buttons">
            <Button onClick={handleViewPricing}>View Pricing</Button>
          </div>
        )}
      </div>

      <div className="plan-usage-details-container-wrapper">
        <Divider>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Features & Benefits
          </Typography.Title>
        </Divider>

        <List
          itemLayout="horizontal"
          dataSource={data}
          size="small"
          split={false}
          bordered={false}
          renderItem={(item) => {
            const IconComponent = item.icon;
            return (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <IconComponent className="credit-selector__list-icon" />
                  }
                  title={item.title}
                />
              </List.Item>
            );
          }}
        />

        <Divider>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Usage Details
          </Typography.Title>
        </Divider>

        <div className="plan-usage-details-container">
          <div className="plan-usage-detail-card">
            <Typography.Title level={3} style={{ margin: 0 }}>
              {jobPostsCount}
              {limits.jobPosts > 0 && `/ ${limits.jobPosts}`}
            </Typography.Title>
            <Typography.Text type="secondary" strong style={{ margin: 0 }}>
              Total Active Job Posts
            </Typography.Text>
          </div>

          {subscriptionMode !== "credit" && (
            <div className="plan-usage-detail-card">
              <Typography.Title level={3} style={{ margin: 0 }}>
                {applicationsCount} /{limits.applicationsProcessed}
              </Typography.Title>
              <Typography.Text type="secondary" strong style={{ margin: 0 }}>
                Total Assessments
              </Typography.Text>
            </div>
          )}

          {subscription && (
            <div className="plan-usage-detail-card">
              <Typography.Title level={3} style={{ margin: 0 }}>
                {recruitersCount}
                {plan.limits.teamMembers > 0 && `/ ${plan.limits.teamMembers}`}
              </Typography.Title>
              <Typography.Text type="secondary" strong style={{ margin: 0 }}>
                Total Recruiters
              </Typography.Text>
            </div>
          )}

          {subscriptionMode === "base" && (
            <div className="plan-usage-detail-card">
              <Typography.Title level={3} style={{ margin: 0 }}>
                {activeAddonsCount}
              </Typography.Title>
              <Typography.Text type="secondary" strong style={{ margin: 0 }}>
                Active Addon Plans
              </Typography.Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanDetails;
